import { Component, forwardRef, Input } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { TextAreaErrorMessages } from './text-area-error-messages';

/**
 * __Usage__:
 * ```html
 * Template driven Form
 * <ls-text-area
 *  [label]="'Text Area'"
 *  [placeHolder]="'This is a placeholder'"
 *  [required]="false"
 *  [maxlength]="50"
 *  [name]="'TextArea'"
 *  ngModel>
 * </ls-text-area>
 * ```
 * ```html
 * Reactive Forms
 * <ls-text-area
 *  [label]="'Text Area'"
 *  [placeHolder]="'This is a placeholder'"
 *  [required]="false"
 *  [maxlength]="50"
 *  formControlName="TextArea">
 * </ls-text-area>
 * ```
 */
@Component({
  selector: 'ls-text-area',
  templateUrl: './text-area.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextAreaComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TextAreaComponent),
      multi: true
    }
  ]
})
export class TextAreaComponent implements ControlValueAccessor {
  @Input()
  public label: string;
  @Input()
  public text = '';
  @Input()
  public placeHolder = '';
  @Input()
  public maxlength: number;
  @Input()
  public required = false;
  public errorMessage: string = TextAreaErrorMessages.NoError;

  public writeValue(value: string): void {
    value !== null ? (this.text = value) : (this.text = '');
  }

  public registerOnChange(fn: Function): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(): void {
    return;
  }

  public propagateChange: any = () => {
    return;
  };

  public validate: any = (control: FormControl) => {
    if (!control.dirty) {
      this.errorMessage = TextAreaErrorMessages.NoError;
      return null;
    }

    if (this.required && this.text.length === 0) {
      this.errorMessage = TextAreaErrorMessages.RequiredField;
      return { valid: false };
    } else {
      this.errorMessage = TextAreaErrorMessages.NoError;
      return null;
    }
  };

  public textValueChanged($event: any): void {
    if ($event.target.value !== this.text) {
      this.text = $event.target.value;
      this.propagateChange(this.text);
    }
  }
}
